import { getQAFileTypeLabel } from '@aloft/toolkit-shared';
import { Button, Flex, Tooltip, Typography } from '@aloft/toolkit-sui';

import { Controller } from 'react-hook-form';

import { useTkv2OrderID } from '../../../controller/hooks/useTkv2OrderID.ts';
import { useZodForm } from '../../../hooks/useZodForm.ts';
import { selectQCDisabledForOrder } from '../../../redux/features/selectors/orderSelectors.ts';
import { useAppSelector } from '../../../redux/hooks.ts';
import { schQAUploadDocumentsForm } from '../schemas.ts';
import { QABetaModuleProps } from '../types.ts';
import { UploadDocumentsField } from './UploadDocumentsField.tsx';
import { UploadDocumentsFileUploader } from './UploadDocumentsFileUploader.tsx';

type UploadDocumentsProps = {
  startQAMutation: QABetaModuleProps['startQAMutation'];
};

export const UploadDocumentsForm = (props: UploadDocumentsProps) => {
  const orderID = useTkv2OrderID();
  const { startQAMutation } = props;
  const disableQCForOrder = useAppSelector(selectQCDisabledForOrder);

  const form = useZodForm({
    schema: schQAUploadDocumentsForm,
    mode: 'onBlur',
    reValidateMode: 'onBlur',
  });

  const handleSubmit = form.handleSubmit(
    async (files) => {
      await startQAMutation.mutateAsync({
        orderID,
        files,
      });
    },
    (error) => console.warn(error),
  );

  const { isValid } = form.formState;

  const isSubmitLoading = startQAMutation.isPending;
  const isSubmitDisabled =
    !isValid || isSubmitLoading || startQAMutation.isSuccess || disableQCForOrder;

  return (
    <form
      style={{
        display: 'flex',
        flexDirection: 'column',
        gap: '16px',
      }}
    >
      <Typography>
        Upload your report (FNMA 1004, 2055, 1004c, 1073, 1075, 1025 Conventional or FHA; GP
        Residential, GP Condo, GP 2-4, GP Vacant Land):
      </Typography>
      <Flex gap="16px" flexWrap="wrap">
        <UploadDocumentsField id="report-pdf" label={getQAFileTypeLabel('reportPDF')} required>
          <Controller
            name="reportPDF"
            control={form.control}
            render={(p) => <UploadDocumentsFileUploader {...p} id="report-pdf" type="pdf" />}
          />
        </UploadDocumentsField>
      </Flex>
      <Typography>For a more comprehensive check, upload the following order documents:</Typography>
      <Flex gap="16px" flexWrap="wrap">
        <UploadDocumentsField id="order-details-pdf" label={getQAFileTypeLabel('orderDetailsPDF')}>
          <Controller
            name="orderDetailsPDF"
            control={form.control}
            render={(p) => <UploadDocumentsFileUploader {...p} id="order-details-pdf" type="pdf" />}
          />
        </UploadDocumentsField>
        <UploadDocumentsField id="loe-pdf" label={getQAFileTypeLabel('loePDF')}>
          <Controller
            name="loePDF"
            control={form.control}
            render={(p) => <UploadDocumentsFileUploader {...p} id="loe-pdf" type="pdf" />}
          />
        </UploadDocumentsField>
        <UploadDocumentsField
          id="purchase-contract-pdf"
          label={getQAFileTypeLabel('purchaseContractPDF')}
        >
          <Controller
            name="purchaseContractPDF"
            control={form.control}
            render={(p) => (
              <UploadDocumentsFileUploader {...p} id="purchase-contract-pdf" type="pdf" />
            )}
          />
        </UploadDocumentsField>
      </Flex>
      <Flex>
        {/* The tooltip floats kind of far away if we don't wrap this in a Flex :/ */}
        <Tooltip
          isDisabled={!disableQCForOrder}
          label="Not available in training orders. Please create an active order."
          placement="right"
        >
          <Button
            type="submit"
            text="Send to QC"
            onClick={handleSubmit}
            disabled={isSubmitDisabled}
            isLoading={isSubmitLoading}
            __dangerDoNotUseSx={{
              alignSelf: 'start',
            }}
          />
        </Tooltip>
      </Flex>
    </form>
  );
};
